import { API_BASE } from '../apiBase';
// ─────────────────────────────────────────────────────────────────────────────
// SurvAIHealth EHR — API client.
//
// Talks to the survai-engine EHR routes (/api/survai-engine/ehr/*). Handles the
// Supabase JWT auth header and the camelCase (frontend) ⇄ snake_case (DB/API)
// field mapping so the rest of the app keeps its camelCase shapes.
//
// The stores use this client offline-first: localStorage is the working cache;
// on load we hydrate from the API when reachable, and writes are mirrored
// best-effort. If the engine is unreachable/unauthorized, the app keeps working
// from localStorage with the seeded demo data.
// ─────────────────────────────────────────────────────────────────────────────

const BASE = API_BASE + "/api/survai-engine/ehr";

async function headers() {
  try {
    const { supabase } = await import("../supabaseClient");
    if (!supabase) return { "Content-Type": "application/json" };
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token
      ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
      : { "Content-Type": "application/json" };
  } catch {
    return { "Content-Type": "application/json" };
  }
}

async function req(method, path, body) {
  const res = await fetch(BASE + path, {
    method,
    headers: await headers(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try { const j = await res.json(); detail = j?.error?.message || detail; } catch { /* ignore */ }
    const err = new Error(`EHR API ${method} ${path} → ${res.status}: ${detail}`);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const json = await res.json();
  return json?.data;
}

// ── field mappers (camelCase ⇄ snake_case) ───────────────────────────────────
const renameOut = (obj, map) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === undefined) continue;
    out[map[k] || k] = v;
  }
  return out;
};
const renameIn = (obj, map) => {
  if (!obj) return obj;
  const inv = Object.fromEntries(Object.entries(map).map(([a, b]) => [b, a]));
  const out = {};
  for (const [k, v] of Object.entries(obj)) out[inv[k] || k] = v;
  return out;
};
const arrIn = (rows, map) => (rows || []).map((r) => renameIn(r, map));

const M = {
  patient: { firstName: "first_name", lastName: "last_name", primaryDx: "primary_dx" },
  assessment: { patientId: "patient_id", instrumentId: "instrument_id", instrumentName: "instrument_name", administeredBy: "administered_by", administeredAt: "administered_at" },
  plan: { patientId: "patient_id" },
  note: { patientId: "patient_id", date: "note_date", signedAt: "signed_at", signedBy: "signed_by" },
  consent: { patientId: "patient_id", signedDate: "signed_date" },
  provider: {},
  room: {},
  payer: { payerId: "payer_id" },
  appointment: { patientId: "patient_id", patientName: "patient_name", providerId: "provider_id", roomId: "room_id", date: "appt_date", start: "start_time", end: "end_time", typeId: "type_id", typeLabel: "type_label", cptCode: "cpt_code", payerId: "payer_id", claimId: "claim_id" },
  policy: { patientId: "patient_id", payerId: "payer_id", payerName: "payer_name", memberId: "member_id", group: "grp", verifiedAt: "verified_at" },
  authorization: { patientId: "patient_id", payerName: "payer_name", authNumber: "auth_number", unitsApproved: "units_approved", unitsUsed: "units_used", startDate: "start_date", endDate: "end_date" },
  claim: { patientId: "patient_id", appointmentId: "appointment_id", dateOfService: "date_of_service", cptCode: "cpt_code", payerId: "payer_id", providerId: "provider_id" },
};

// ── reachability ─────────────────────────────────────────────────────────────
export async function ping() {
  try { await req("GET", "/patients"); return true; } catch { return false; }
}

// ── patients ─────────────────────────────────────────────────────────────────
export const listPatients = async () => arrIn(await req("GET", "/patients"), M.patient);
export const createPatient = async (p) => renameIn(await req("POST", "/patients", renameOut(p, M.patient)), M.patient);
export const updatePatient = async (id, p) => renameIn(await req("PATCH", `/patients/${id}`, renameOut(p, M.patient)), M.patient);
export const deletePatient = (id) => req("DELETE", `/patients/${id}`);

// ── assessments ──────────────────────────────────────────────────────────────
export const listAssessments = async (pid) => arrIn(await req("GET", `/patients/${pid}/assessments`), M.assessment);
export const createAssessment = async (pid, a) => renameIn(await req("POST", `/patients/${pid}/assessments`, renameOut(a, M.assessment)), M.assessment);

// ── treatment plans ──────────────────────────────────────────────────────────
export const listPlans = async (pid) => arrIn(await req("GET", `/patients/${pid}/treatment-plans`), M.plan);
export const createPlan = async (pid, p) => renameIn(await req("POST", `/patients/${pid}/treatment-plans`, renameOut(p, M.plan)), M.plan);
export const updatePlan = async (id, p) => renameIn(await req("PATCH", `/treatment-plans/${id}`, renameOut(p, M.plan)), M.plan);

// ── notes ────────────────────────────────────────────────────────────────────
export const listNotes = async (pid) => arrIn(await req("GET", `/patients/${pid}/notes`), M.note);
export const createNote = async (pid, n) => renameIn(await req("POST", `/patients/${pid}/notes`, renameOut(n, M.note)), M.note);
export const signNote = async (id, signedBy) => renameIn(await req("POST", `/notes/${id}/sign`, { signed_by: signedBy }), M.note);
export const addAddendum = async (id, text, by) => renameIn(await req("POST", `/notes/${id}/addenda`, { text, by }), M.note);

// ── consents ─────────────────────────────────────────────────────────────────
export const listConsents = async (pid) => arrIn(await req("GET", `/patients/${pid}/consents`), M.consent);
export const createConsent = async (pid, c) => renameIn(await req("POST", `/patients/${pid}/consents`, renameOut(c, M.consent)), M.consent);

// ── reference ────────────────────────────────────────────────────────────────
export const listProviders = async () => arrIn(await req("GET", "/providers"), M.provider);
export const createProvider = async (p) => renameIn(await req("POST", "/providers", p), M.provider);
export const listRooms = async () => arrIn(await req("GET", "/rooms"), M.room);
export const createRoom = async (r) => renameIn(await req("POST", "/rooms", r), M.room);
export const listPayers = async () => arrIn(await req("GET", "/payers"), M.payer);
export const createPayer = async (p) => renameIn(await req("POST", "/payers", renameOut(p, M.payer)), M.payer);

// ── scheduling ───────────────────────────────────────────────────────────────
export const listAppointments = async (date) => arrIn(await req("GET", `/appointments${date ? `?date=${date}` : ""}`), M.appointment);
export const createAppointment = async (a) => renameIn(await req("POST", "/appointments", renameOut(a, M.appointment)), M.appointment);
export const updateAppointment = async (id, a) => renameIn(await req("PATCH", `/appointments/${id}`, renameOut(a, M.appointment)), M.appointment);
export const setAppointmentStatus = async (id, status) => renameIn(await req("POST", `/appointments/${id}/status`, { status }), M.appointment);

// ── insurance ────────────────────────────────────────────────────────────────
export const listPolicies = async (pid) => arrIn(await req("GET", `/patients/${pid}/policies`), M.policy);
export const createPolicy = async (pid, p) => renameIn(await req("POST", `/patients/${pid}/policies`, renameOut(p, M.policy)), M.policy);
export const verifyPolicy = async (id) => renameIn(await req("POST", `/policies/${id}/verify`, {}), M.policy);
export const listAuthorizations = async (pid) => arrIn(await req("GET", `/patients/${pid}/authorizations`), M.authorization);
export const createAuthorization = async (pid, a) => renameIn(await req("POST", `/patients/${pid}/authorizations`, renameOut(a, M.authorization)), M.authorization);

// ── billing ──────────────────────────────────────────────────────────────────
export const listClaims = async (pid) => arrIn(await req("GET", `/claims${pid ? `?patientId=${pid}` : ""}`), M.claim);
export const createClaim = async (c) => renameIn(await req("POST", "/claims", renameOut(c, M.claim)), M.claim);
export const advanceClaim = async (id, status, payment) => renameIn(await req("POST", `/claims/${id}/advance`, { status, payment }), M.claim);

// ── audit ────────────────────────────────────────────────────────────────────
export const listAudit = async () => await req("GET", "/audit");

const ehrApi = {
  ping, listPatients, createPatient, updatePatient, deletePatient,
  listAssessments, createAssessment, listPlans, createPlan, updatePlan,
  listNotes, createNote, signNote, addAddendum, listConsents, createConsent,
  listProviders, createProvider, listRooms, createRoom, listPayers, createPayer,
  listAppointments, createAppointment, updateAppointment, setAppointmentStatus,
  listPolicies, createPolicy, verifyPolicy, listAuthorizations, createAuthorization,
  listClaims, createClaim, advanceClaim, listAudit,
};
export default ehrApi;
